import { useEffect, useRef } from 'react';
import { useShellRouter } from '@/hooks/useShellRouter';
import { installShellBridge, SHELL_NAVIGATED_EVENT } from '@/utils/shellNavigation';

/**
 * Publishes `window.siyaShell` for the remote Apps, and announces completed route changes
 * on `window`. See utils/shellNavigation for what both are for and why the WebView case
 * needs them.
 *
 * Mounted for the lifetime of the app and renders nothing. The router is held in a ref so
 * the bridge is installed once — re-installing it on every route change would swap the
 * object under an App that captured it.
 */
export default function ShellNavBridge() {
  const router = useShellRouter();
  const routerRef = useRef(router);
  routerRef.current = router;

  useEffect(() => {
    return installShellBridge((path) => {
      // Fire-and-forget: a rejected push is a route that refused to load, which the
      // router has already reported. Nothing useful to do at the call site.
      void routerRef.current.push(path);
    });
  }, []);

  useEffect(() => {
    const announce = () => window.dispatchEvent(new Event(SHELL_NAVIGATED_EVENT));
    // routeChangeComplete, not the push itself: the event has to mean the URL has already
    // changed, because a listener reads window.location when it fires. It also covers
    // navigations this bridge did not make — sidebar links, the back button.
    router.events.on('routeChangeComplete', announce);
    return () => router.events.off('routeChangeComplete', announce);
  }, [router.events]);

  return null;
}
